import React from 'react';
import {
    PLAN_TYPE_CODES,
    findOptionByValue,
    BILLING_CURRENCY_CODES,
    getBillingStatusDisplay,
    formatDate,
    bigAmountFormat,
    DEFAULT_BILLING_CURRENCY,
    resolveInvoiceTaxSnapshot,
 formatMetricPrice,
} from '../../utils';

const cell = { padding: '8px', borderBottom: '1px solid #f3f4f6' };
const th = { padding: '8px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 12 };
const muted = { color: '#6b7280' };

const AdminUserPricingDisplay = ({ user }) => {
    const pricingProfile = user?.pricing_profile;
    const sub = user?.sub;
    const metrics = user?.usage_metrics || user?.usage_status?.metrics || [];
    const estimate = user?.usage_status?.estimate;
    const period = user?.billing_period || user?.usage_status?.period;
    const planType = Number(pricingProfile?.plan_type ?? sub?.plan_type);
    const isFree = planType === PLAN_TYPE_CODES[0].value;
    const billingCycle = user?.billing_cycle_label || pricingProfile?.billing_cycle || sub?.billing_cycle || 'monthly';
    const currencyEntry = findOptionByValue(BILLING_CURRENCY_CODES, pricingProfile?.currency ?? estimate?.currency ?? sub?.currency);
    const currencySymbol = currencyEntry?.symbol ?? DEFAULT_BILLING_CURRENCY?.symbol;
    const billingStatus = getBillingStatusDisplay(sub?.status);
    const rateRows = isFree
        ? (pricingProfile?.free_limits || [])
        : (pricingProfile?.custom_pricing || pricingProfile?.pricing || []);

    const usageByType = {};
    for (const m of metrics) {
        usageByType[Number(m.type)] = Number(m.usage ?? m.count ?? 0);
    }

    const seen = new Set();
    const tableRows = [];
    for (const row of rateRows) {
        const type = Number(row.type);
        seen.add(type);
        tableRows.push({
            type,
            label: row.type_label || row.label,
            usage: usageByType[type] ?? 0,
            price: row.price,
            limit_value: row.limit_value,
            over_limit: metrics.find((m) => Number(m.type) === type)?.over_limit,
        });
    }
    for (const m of metrics) {
        const type = Number(m.type);
        if (seen.has(type)) continue;
        tableRows.push({
            type,
            label: m.label,
            usage: Number(m.usage ?? m.count ?? 0),
            price: null,
            limit_value: m.free_limit,
            over_limit: m.over_limit,
        });
    }

    let subtotal = 0;
    if (!isFree) {
        for (const row of tableRows) {
            const rate = parseFloat(row.price);
            if (!Number.isFinite(rate)) continue;
            subtotal += row.type === 0 ? rate : (row.usage || 0) * rate;
        }
        subtotal = Math.round(subtotal * 100) / 100;
    }

    const tax = !isFree && subtotal >= 0
        ? resolveInvoiceTaxSnapshot(
            {
                subtotal_before_tax: estimate?.subtotal_before_tax ?? estimate?.total ?? subtotal,
                gst_percent: estimate?.gst_percent,
                gst_amount: estimate?.gst_amount,
                estimate: {
                    total: estimate?.total ?? subtotal,
                    subtotal_before_tax: estimate?.subtotal_before_tax ?? estimate?.total ?? subtotal,
                    gst_percent: estimate?.gst_percent,
                    gst_amount: estimate?.gst_amount,
                    grand_total: estimate?.grand_total,
                },
            },
            estimate?.grand_total
        )
        : null;

    return (
        <div>
            <div style={{ fontSize: 13 }}>
                <div className="row">
                    <div className="col-6 mb-2"><span style={muted}>Plan:</span> <strong>{pricingProfile?.plan_label || user?.plan_display || 'Free'}</strong></div>
                    <div className="col-6 mb-2">
                        <span style={muted}>Billing cycle:</span>{' '}
                        <strong style={{ textTransform: 'capitalize' }}>{billingCycle}</strong>
                    </div>
                    {currencyEntry ? (
                        <div className="col-6 mb-2"><span style={muted}>Currency:</span> <strong>{currencyEntry.label}</strong></div>
                    ) : null}
                    {period?.start || period?.end ? (
                        <div className="col-6 mb-2">
                            <span style={muted}>Period:</span>{' '}
                            <strong>{formatDate(period?.start)} – {formatDate(period?.end)}</strong>
                        </div>
                    ) : null}
                    <div className="col-6 mb-2"><span style={muted}>Next bill date:</span> <strong>{formatDate(sub?.next_billing_date) || '—'}</strong></div>
                    <div className="col-6 mb-2">
                        <span style={muted}>Status:</span>{' '}
                        {billingStatus.badgeClass ? (
                            <span className={billingStatus.badgeClass}>{billingStatus.label}</span>
                        ) : (
                            <strong>{billingStatus.label}</strong>
                        )}
                    </div>
                    <div className="col-6 mb-2"><span style={muted}>Total paid:</span> <strong style={{ color: '#059669' }}>{bigAmountFormat(user?.payment_received ?? sub?.total_payments ?? 0, currencySymbol)}</strong></div>
                    <div className="col-6 mb-2"><span style={muted}>Last payment:</span> <strong>{formatDate(user?.last_payment_at || sub?.last_payment_date) || '—'}</strong></div>
                </div>
            </div>

            {tableRows.length > 0 ? (
                <>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: '#f3f4f6' }}>
                                <th style={th}>Metric</th>
                                <th style={{ ...th, textAlign: 'right' }}>Usage</th>
                                <th style={{ ...th, textAlign: 'right' }}>{isFree ? 'Limit / mo' : 'Rate'}</th>
                                <th style={{ ...th, textAlign: 'right' }}>{isFree ? 'Over limit' : 'Amount'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tableRows.map((row) => {
                                const isBase = row.type === 0;
                                const usage = row.usage || 0;
                                const rate = parseFloat(row.price);
                                const line = isBase
                                    ? (Number.isFinite(rate) ? rate : 0)
                                    : (Number.isFinite(rate) ? usage * rate : 0);
                                return (
                                    <tr key={row.type}>
                                        <td style={cell}>{row.label}</td>
                                        <td style={{ ...cell, textAlign: 'right' }}>{isBase && !isFree ? '—' : usage.toLocaleString()}</td>
                                        <td style={{ ...cell, textAlign: 'right' }}>
                                            {isFree
                                                ? (row.limit_value != null ? Number(row.limit_value).toLocaleString() : '—')
                                                : (Number.isFinite(rate) ? formatMetricPrice(currencySymbol, rate) : '—')}
                                        </td>
                                        <td style={{ ...cell, textAlign: 'right' }}>
                                            {isFree
                                                ? Number(row.over_limit || 0).toLocaleString()
                                                : bigAmountFormat(line, currencySymbol)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {tax ? (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                            <div style={{ minWidth: 220, fontSize: 13 }}>
                                <div style={{ fontWeight: 700, textAlign: 'right', marginBottom: 8, color: '#374151' }}>TOTALS</div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, padding: '4px 0', color: '#6b7280' }}>
                                    <span>Subtotal:</span>
                                    <span>{bigAmountFormat(tax.subtotal_before_tax, currencySymbol)}</span>
                                </div>
                                {(tax.gst_percent > 0 || tax.gst_amount > 0) && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, padding: '4px 0', color: '#6b7280' }}>
                                        <span>GST ({tax.gst_percent}%):</span>
                                        <span>{bigAmountFormat(tax.gst_amount, currencySymbol)}</span>
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, padding: '6px 0 0', fontWeight: 700, color: '#111827', borderTop: '1px solid #e5e7eb', marginTop: 4 }}>
                                    <span>Total:</span>
                                    <span style={{ color: '#4f46e5' }}>{bigAmountFormat(tax.grand_total, currencySymbol)}</span>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </>
            ) : (
                <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>No pricing data available.</p>
            )}
        </div>
    );
};

export default AdminUserPricingDisplay;
